import React, { useEffect, useMemo, useState } from 'react';
import { BsChevronLeft, BsChevronRight, BsCloudArrowUp } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Button, Flex, Icon, PopoverBody, PopoverHeader, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../constants/cypressIds';
import { RequirementTemplatesDropdownDuskIds } from '../../constants/duskIds';
import { useRequirementTemplateFolderData } from '../../services/requirement-template-service';
import { getInfiniteListData } from '../../services/util';
import colors from '../../styles/theme/foundation/colors';
import { buttonItemStyle } from '../../styles/theme/util/popoverMenuButtonStyles';
import PINSAIOutlineGradientButton from '../PINSAIOutlineGradientButton';
import SearchInputGroup from '../SearchInputGroup';
import LoadingSpinner from '../Spinner';

import TemplateContent from './TemplateContent';

import './styles.scss';

const Content = ({ onChange, companyId, onUploadContract }) => {
  const [folderId, setFolderId] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [folderDisplayName, setDisplayName] = useState('');
  const [search, setSearch] = useState('');

  const {
    data: folderPaginationData,
    isLoading: foldersLoading,
    fetchNextPage: fetchNextFolders,
    hasNextPage: foldersHasMore
  } = useRequirementTemplateFolderData({ companyId });

  const folderData = useMemo(() => {
    if (foldersLoading) {
      return [];
    }

    return getInfiniteListData(folderPaginationData);
  }, [folderPaginationData, foldersLoading]);

  useEffect(() => {
    if (folderName.length > 15) {
      setDisplayName(`${folderName.substring(0, 15)}...`);
    } else {
      setDisplayName(folderName);
    }
  }, [folderName]);

  const handleInputChange = template => {
    onChange(template);
    setSearch('');
    setFolderId(null);
  };

  if (folderId) {
    return (
      <>
        <PopoverHeader alignItems="center" as={Flex} justifyContent="space-between" px={2} py={3}>
          <Tooltip label={folderName.length > folderDisplayName.length && folderName} placement="top">
            <Button
              flexBasis="35%"
              justifyContent="flex-start"
              leftIcon={<Icon as={BsChevronLeft} />}
              mr={4}
              pl={0}
              variant="textPrimary"
              width="100%"
              onClick={() => {
                setFolderId(null);
                setFolderName('');
              }}
            >
              {folderDisplayName}
            </Button>
          </Tooltip>
          <SearchInputGroup
            cypressId={CypressIds.reqTemplateSearchInput}
            flexBasis="55%"
            searchTerm={search}
            variant="primary"
            onChange={evt => setSearch(evt.target.value)}
          />
        </PopoverHeader>
        <PopoverBody maxHeight="200px" overflowY="auto" width="400px">
          <TemplateContent
            companyId={companyId}
            folderId={folderId}
            handleInputChange={handleInputChange}
            searchInput={search}
          />
        </PopoverBody>
      </>
    );
  }

  return (
    <PopoverBody color={colors.greyScale['600']} textStyle="bodyMd500" width="400px">
      {onUploadContract && (
        <PINSAIOutlineGradientButton
          data-cy={CypressIds.reqTemplateUploadContractButton}
          leftIcon={BsCloudArrowUp}
          mb={2}
          mt={0}
          text="Upload Contract"
          onClick={onUploadContract}
        />
      )}
      <Box className="template-folders-dropdown-menu">
        {folderData.length
          ? (
            <InfiniteScroll
              dataLength={folderData.length}
              hasMore={foldersHasMore}
              height="200px"
              loader={(<LoadingSpinner isInline />)}
              next={fetchNextFolders}
              scrollableTarget="template-folders-dropdown-menu"
            >
              {folderData.map(folder => (
                <Flex
                  _hover={buttonItemStyle.hover}
                  alignItems="center"
                  data-cy={CypressIds.reqTemplateGetFolderOptionByName(folder.name)}
                  dusk={RequirementTemplatesDropdownDuskIds.getFolderOptionByName(folder.name)}
                  justifyContent="space-between"
                  key={folder.id}
                  padding="10px 14px"
                  width="100%"
                  onClick={() => {
                    setFolderName(folder.name);
                    setFolderId(folder.id);
                  }}
                >
                  <Text>{folder.name}</Text>
                  <Icon as={BsChevronRight} />
                </Flex>
              ))}
            </InfiniteScroll>
          ) : <Text>No folders</Text>}
      </Box>
    </PopoverBody>
  );
};

/** Props come from parent component and are not directly passed */
Content.propTypes = {
  companyId: PropTypes.number,
  onChange: PropTypes.func,
  onUploadContract: PropTypes.func
};

export default Content;
